import { useState } from 'react';
import {
  Plus,
  Search,
  Filter,
  Edit2,
  Trash2,
  Eye,
  Calendar,
  Tag,
  MoreVertical,
  CheckCircle,
  Clock,
  X,
  Save,
  Image as ImageIcon,
} from 'lucide-react';
import { cn } from '@/lib/cn';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  category: string;
  tags: string[];
  status: 'draft' | 'published' | 'scheduled';
  publishDate: Date;
  views: number;
  image?: string;
}

// Demo data
const initialPosts: BlogPost[] = [
  {
    id: '1',
    title: 'Yapay Zeka ile Yazilim Gelistirmenin Gelecegi',
    slug: 'yapay-zeka-yazilim-gelistirme',
    excerpt: 'AI destekli kod yazma araclari ve bunlarin yazilim gelistirme sureclerine etkileri...',
    content: 'Lorem ipsum dolor sit amet...',
    author: 'Ahmet Yilmaz',
    category: 'Teknoloji',
    tags: ['AI', 'Yazilim', 'Gelecek'],
    status: 'published',
    publishDate: new Date('2024-01-15'),
    views: 1234,
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400',
  },
  {
    id: '2',
    title: 'Modern Web Gelistirme Trendleri 2024',
    slug: 'web-gelistirme-trendleri-2024',
    excerpt: 'Bu yil one cikan web teknolojileri ve frameworkler hakkinda detayli bir inceleme...',
    content: 'Lorem ipsum dolor sit amet...',
    author: 'Mehmet Demir',
    category: 'Web',
    tags: ['React', 'Next.js', 'TypeScript'],
    status: 'published',
    publishDate: new Date('2024-01-10'),
    views: 892,
    image: 'https://images.unsplash.com/photo-1627398242454-45a1465c2479?w=400',
  },
  {
    id: '3',
    title: 'Bulut Bilisim ve Kubernetes',
    slug: 'bulut-bilisim-kubernetes',
    excerpt: 'Konteyner orkestrasyon araci Kubernetes ile olceklenebilir uygulamalar...',
    content: 'Lorem ipsum dolor sit amet...',
    author: 'Ayse Kaya',
    category: 'DevOps',
    tags: ['Kubernetes', 'Docker', 'Cloud'],
    status: 'draft',
    publishDate: new Date('2024-01-20'),
    views: 0,
  },
  {
    id: '4',
    title: 'Siber Guvenlik Temelleri',
    slug: 'siber-guvenlik-temelleri',
    excerpt: 'Her yazilimcinin bilmesi gereken temel siber guvenlik prensipleri...',
    content: 'Lorem ipsum dolor sit amet...',
    author: 'Ali Ozturk',
    category: 'Guvenlik',
    tags: ['Security', 'Best Practices'],
    status: 'scheduled',
    publishDate: new Date('2024-02-01'),
    views: 0,
  },
];

const categories = ['Teknoloji', 'Web', 'DevOps', 'Guvenlik', 'Mobile', 'AI/ML'];
const allTags = ['AI', 'Yazilim', 'React', 'Next.js', 'TypeScript', 'Kubernetes', 'Docker', 'Cloud', 'Security'];

export function AdminBlog() {
  const [posts, setPosts] = useState<BlogPost[]>(initialPosts);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [isEditing, setIsEditing] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Filter posts
  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || post.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || post.category === categoryFilter;
    return matchesSearch && matchesStatus && matchesCategory;
  });

  const handleEdit = (post: BlogPost) => {
    setEditingPost({ ...post });
    setIsEditing(true);
  };

  const handleCreate = () => {
    const newPost: BlogPost = {
      id: Date.now().toString(),
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      author: 'Admin',
      category: categories[0],
      tags: [],
      status: 'draft',
      publishDate: new Date(),
      views: 0,
    };
    setEditingPost(newPost);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!editingPost) return;

    // Generate slug from title if empty
    if (!editingPost.slug) {
      editingPost.slug = editingPost.title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
    }

    const existingIndex = posts.findIndex(p => p.id === editingPost.id);
    if (existingIndex >= 0) {
      const updated = [...posts];
      updated[existingIndex] = editingPost;
      setPosts(updated);
    } else {
      setPosts([editingPost, ...posts]);
    }
    setIsEditing(false);
    setEditingPost(null);
  };

  const handleDelete = (id: string) => {
    setPosts(posts.filter(p => p.id !== id));
    setShowDeleteConfirm(null);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-500/10 text-green-500">
            <CheckCircle size={12} /> Yayinda
          </span>
        );
      case 'draft':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-yellow-500/10 text-yellow-500">
            <Clock size={12} /> Taslak
          </span>
        );
      case 'scheduled':
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-blue-500/10 text-blue-500">
            <Calendar size={12} /> Zamanlanmis
          </span>
        );
      default:
        return null;
    }
  };

  // Edit Modal
  if (isEditing && editingPost) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">
              {editingPost.id ? 'Blog Yazisi Duzenle' : 'Yeni Blog Yazisi'}
            </h1>
            <p className="text-text-secondary">Blog icerigini olusturun veya duzenleyin</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setIsEditing(false); setEditingPost(null); }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border-default text-text-secondary hover:text-text-primary transition-colors"
            >
              <X size={18} /> Iptal
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90 transition-colors"
            >
              <Save size={18} /> Kaydet
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-bg-card border border-border-default rounded-xl p-6">
              <label className="block text-sm font-medium text-text-primary mb-2">Baslik</label>
              <input
                type="text"
                value={editingPost.title}
                onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
                placeholder="Blog yazisi basligi..."
                className="w-full px-4 py-3 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
              />
            </div>

            {/* Slug */}
            <div className="bg-bg-card border border-border-default rounded-xl p-6">
              <label className="block text-sm font-medium text-text-primary mb-2">URL Slug</label>
              <div className="flex items-center gap-2">
                <span className="text-text-tertiary">/blog/</span>
                <input
                  type="text"
                  value={editingPost.slug}
                  onChange={(e) => setEditingPost({ ...editingPost, slug: e.target.value })}
                  placeholder="yazi-slug"
                  className="flex-1 px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
            </div>

            {/* Excerpt */}
            <div className="bg-bg-card border border-border-default rounded-xl p-6">
              <label className="block text-sm font-medium text-text-primary mb-2">Ozet</label>
              <textarea
                value={editingPost.excerpt}
                onChange={(e) => setEditingPost({ ...editingPost, excerpt: e.target.value })}
                placeholder="Kisa ozet..."
                rows={3}
                className="w-full px-4 py-3 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none resize-none"
              />
            </div>

            {/* Content */}
            <div className="bg-bg-card border border-border-default rounded-xl p-6">
              <label className="block text-sm font-medium text-text-primary mb-2">Icerik</label>
              <textarea
                value={editingPost.content}
                onChange={(e) => setEditingPost({ ...editingPost, content: e.target.value })}
                placeholder="Blog icerigini yazin..."
                rows={15}
                className="w-full px-4 py-3 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none resize-none font-mono text-sm"
              />
              <p className="mt-2 text-xs text-text-tertiary">Markdown desteklenir</p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <div className="bg-bg-card border border-border-default rounded-xl p-6">
              <label className="block text-sm font-medium text-text-primary mb-3">Durum</label>
              <select
                value={editingPost.status}
                onChange={(e) => setEditingPost({ ...editingPost, status: e.target.value as BlogPost['status'] })}
                className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
              >
                <option value="draft">Taslak</option>
                <option value="published">Yayinla</option>
                <option value="scheduled">Zamanla</option>
              </select>

              {editingPost.status === 'scheduled' && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-text-primary mb-2">Yayin Tarihi</label>
                  <input
                    type="datetime-local"
                    value={editingPost.publishDate.toISOString().slice(0, 16)}
                    onChange={(e) => setEditingPost({ ...editingPost, publishDate: new Date(e.target.value) })}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>
              )}
            </div>

            {/* Category */}
            <div className="bg-bg-card border border-border-default rounded-xl p-6">
              <label className="block text-sm font-medium text-text-primary mb-3">Kategori</label>
              <select
                value={editingPost.category}
                onChange={(e) => setEditingPost({ ...editingPost, category: e.target.value })}
                className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div className="bg-bg-card border border-border-default rounded-xl p-6">
              <label className="block text-sm font-medium text-text-primary mb-3">Etiketler</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {editingPost.tags.map(tag => (
                  <span
                    key={tag}
                    className="flex items-center gap-1 px-2 py-1 rounded-full bg-accent/10 text-accent text-sm"
                  >
                    {tag}
                    <button
                      onClick={() => setEditingPost({
                        ...editingPost,
                        tags: editingPost.tags.filter(t => t !== tag)
                      })}
                      className="hover:text-red-500"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value && !editingPost.tags.includes(e.target.value)) {
                    setEditingPost({
                      ...editingPost,
                      tags: [...editingPost.tags, e.target.value]
                    });
                  }
                }}
                className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
              >
                <option value="">Etiket ekle...</option>
                {allTags.filter(t => !editingPost.tags.includes(t)).map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>

            {/* Featured Image */}
            <div className="bg-bg-card border border-border-default rounded-xl p-6">
              <label className="block text-sm font-medium text-text-primary mb-3">One Cikan Gorsel</label>
              {editingPost.image ? (
                <div className="relative">
                  <img
                    src={editingPost.image}
                    alt="Featured"
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => setEditingPost({ ...editingPost, image: undefined })}
                    className="absolute top-2 right-2 p-1 rounded-full bg-red-500 text-white hover:bg-red-600"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-border-default rounded-lg p-6 text-center">
                  <ImageIcon className="mx-auto mb-2 text-text-tertiary" size={32} />
                  <p className="text-sm text-text-tertiary">Gorsel yuklemek icin tiklayin</p>
                </div>
              )}
            </div>

            {/* Author */}
            <div className="bg-bg-card border border-border-default rounded-xl p-6">
              <label className="block text-sm font-medium text-text-primary mb-3">Yazar</label>
              <input
                type="text"
                value={editingPost.author}
                onChange={(e) => setEditingPost({ ...editingPost, author: e.target.value })}
                className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Blog Yonetimi</h1>
          <p className="text-text-secondary">Blog yazilarini olusturun, duzenleyin ve yonetin</p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90 transition-colors"
        >
          <Plus size={18} /> Yeni Yazi
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
          <input
            type="text"
            placeholder="Yazi ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
          >
            <option value="all">Tum Durumlar</option>
            <option value="published">Yayinda</option>
            <option value="draft">Taslak</option>
            <option value="scheduled">Zamanlanmis</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
          >
            <option value="all">Tum Kategoriler</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Toplam Yazi', value: posts.length, color: 'text-accent' },
          { label: 'Yayinda', value: posts.filter(p => p.status === 'published').length, color: 'text-green-500' },
          { label: 'Taslak', value: posts.filter(p => p.status === 'draft').length, color: 'text-yellow-500' },
          { label: 'Zamanlanmis', value: posts.filter(p => p.status === 'scheduled').length, color: 'text-blue-500' },
        ].map((stat, index) => (
          <div key={index} className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
            <p className={cn('text-2xl font-bold', stat.color)}>{stat.value}</p>
            <p className="text-sm text-text-secondary">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Posts List */}
      <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-bg-elevated">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Yazi</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Kategori</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Durum</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Tarih</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Goruntulenme</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Islemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default">
              {filteredPosts.map((post) => (
                <tr key={post.id} className="hover:bg-bg-elevated/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {post.image ? (
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-bg-elevated flex items-center justify-center">
                          <ImageIcon size={20} className="text-text-tertiary" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-text-primary line-clamp-1">{post.title}</p>
                        <p className="text-sm text-text-tertiary line-clamp-1">{post.excerpt}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 rounded-full bg-accent/10 text-accent text-xs">
                      {post.category}
                    </span>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(post.status)}</td>
                  <td className="px-6 py-4 text-sm text-text-secondary">
                    {post.publishDate.toLocaleDateString('tr-TR')}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-text-primary">
                    {post.views.toLocaleString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(post)}
                        className="p-2 rounded-lg hover:bg-bg-elevated text-text-tertiary hover:text-accent transition-colors"
                        title="Duzenle"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        className="p-2 rounded-lg hover:bg-bg-elevated text-text-tertiary hover:text-accent transition-colors"
                        title="Onizle"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(post.id)}
                        className="p-2 rounded-lg hover:bg-red-500/10 text-text-tertiary hover:text-red-500 transition-colors"
                        title="Sil"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-text-tertiary">Yazi bulunamadi</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-bg-card border border-border-default rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-text-primary mb-2">Yaziyi Sil</h3>
            <p className="text-text-secondary mb-6">
              Bu yaziyi silmek istediginizden emin misiniz? Bu islem geri alinamaz.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 rounded-lg border border-border-default text-text-secondary hover:text-text-primary transition-colors"
              >
                Iptal
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
